import { formatEther, formatUnits, parseUnits } from 'viem'

export const SECONDS_PER_YEAR = 31_556_952n

// Alternative to Number.toFixed that doesn't use scientific notation for excessively small or large numbers.
const toFixed = (x: number, decimals: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'decimal',
    useGrouping: false,
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(x)

const safeParseNumber = (value: number, decimals = 18) =>
  safeParseUnits(toFixed(value, decimals), decimals)

const safeParseUnits = (strValue: string, decimals = 18) => {
  if (!/[-+]?[0-9]*\.?[0-9]+/.test(strValue))
    throw Error(`invalid number: ${strValue}`)

  let [whole, dec = ''] = strValue.split('.')

  dec = dec.slice(0, decimals)

  return parseUnits(
    [whole || '0', dec].filter((v) => v.length > 0).join('.'),
    decimals,
  )
}

/**
 * Returns the per-second rate continuously compounded over a year (scaled by WAD),
 * as calculated in Morpho Blue assuming the market is frequently accrued onchain.
 * @param rate The per-second rate to compound annually (scaled by WAD).
 */
// TODO: return a Number for APYs.
export function rateToApy(rate: bigint) {
  return safeParseNumber(
    Math.expm1(+formatEther(BigInt(rate) * SECONDS_PER_YEAR)),
  )
}

export function parseLtv(ltv: bigint | number | string) {
  let str = 0
  try {
    str = Number(formatUnits(BigInt(ltv), 18))
  } catch (e) {}
  return str
}

/**
 * Morpho Blue liquidation incentive factor (LIF) and penalty fraction.
 * The protocol defines LIF = min(1.15, 1 / (0.3 * lltv + 0.7)) where `lltv`
 * is the loan-to-value as a 0..1 fraction. The liquidation penalty a
 * liquidator earns is the fraction `LIF - 1` (e.g. 0.05 = 5%).
 * Moolah (Lista) reuses the same Morpho-style constants.
 */
export function liquidationPenaltyFromLltv(lltv: number) {
  if (!lltv || lltv <= 0) return 0
  const lif = Math.min(1.15, 1 / (0.3 * lltv + 0.7))
  return lif - 1
}

export function formatNr(n: string | bigint, d: number) {
  return Number(formatUnits(BigInt(n ?? 0), d))
}
